import { useState } from 'react'

interface HydrantImportModalProps {
  isOpen: boolean
  token: string | null
  clientId: number | null
  onClose: () => void
  onImported: () => void
}

export default function HydrantImportModal({ isOpen, token, clientId, onClose, onImported }: HydrantImportModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const [updateOnly, setUpdateOnly] = useState(false)
  const [result, setResult] = useState<{ imported: number; updated: number; errors: string[] } | null>(null)
  const [error, setError] = useState('')

  if (!isOpen) return null

  const reset = () => {
    setFile(null)
    setResult(null)
    setError('')
    setUpdateOnly(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true)
    setError('')
    try {
      const response = await fetch('/api/network-hydrants/template', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        setError('Nie udało się pobrać szablonu')
        return
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'szablon_hydranty.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      setError('Wystąpił problem z pobieraniem szablonu')
    } finally {
      setDownloadingTemplate(false)
    }
  }

  const handleImport = async () => {
    if (!file) {
      setError('Wybierz plik XLSX')
      return
    }
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (clientId != null) formData.append('client_id', String(clientId))
      if (updateOnly) formData.append('update_only', 'true')

      const response = await fetch('/api/network-hydrants/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      const data = await response.json()
      if (!response.ok) {
        setError(data.detail || 'Błąd importu')
        return
      }
      setResult({ imported: data.imported_count ?? 0, updated: data.updated_count ?? 0, errors: data.errors ?? [] })
      onImported()
    } catch {
      setError('Wystąpił problem z importem pliku')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={handleClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-lg flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">📥 Import hydrantów (XLSX)</h2>
          <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p className="font-medium mb-1">Format pliku (kolumny):</p>
            <p>Data pomiaru • Numer • Ciśnienie dynamiczne [bar] • Wydajność [l/s] • Ciśnienie statyczne [bar] • Przybliżony adres • Lokalizacja (GPS) • Rodzaj • Spełnia wymagania…? • Uwagi</p>
            <p className="mt-2 text-blue-700">System nadaje własne numery (H-0001…). Kolumna <strong>Numer</strong> (np. <strong>T1</strong>) służy do dopasowania przy kolejnych importach: istniejący hydrant zostaje <strong>zaktualizowany</strong> (data, pomiary, wynik kontroli), nowy — utworzony jako punkt na mapie. Przy braku numeru dopasowanie następuje po współrzędnych.</p>
          </div>

          <label className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={updateOnly}
              onChange={e => setUpdateOnly(e.target.checked)}
              className="mt-0.5 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
            />
            <span className="text-sm text-gray-800">
              <span className="font-medium">Tylko aktualizuj istniejące</span> (nie twórz nowych)
              <span className="block text-xs text-gray-600 mt-0.5">Zaznacz, aby ponownie wczytać ten sam plik i uzupełnić dane (np. wynik kontroli) bez ryzyka duplikatów. Wiersze bez dopasowania zostaną pominięte.</span>
            </span>
          </label>

          <button
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className="w-full flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
          >
            ⬇️ {downloadingTemplate ? 'Pobieranie…' : 'Pobierz szablon XLSX'}
          </button>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plik XLSX z hydrantami</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError('') }}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {file && <p className="mt-1 text-sm text-gray-600">Wybrano: {file.name}</p>}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">{error}</div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm">
              <p className="font-medium text-green-800">Utworzono: {result.imported} • Zaktualizowano: {result.updated}</p>
              {result.errors.length > 0 && (
                <div className="mt-2 text-orange-700">
                  <p className="font-medium">Pominięto wiersze ({result.errors.length}):</p>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={handleClose}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {result ? 'Zamknij' : 'Anuluj'}
          </button>
          <button
            onClick={handleImport}
            disabled={loading || !file}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Importowanie…' : 'Importuj'}
          </button>
        </div>
      </div>
    </div>
  )
}
